import { useCallback, useContext, useEffect, useRef, useState } from 'react'

import UserContext from '../../../context/User/User'
import FlexPackDetailsContext from '../../../context/FlexPackDetails/FlexPackDetails'

import { useModal } from '../../../providers/ModalProvider/ModalProvider'
import { usePagination } from '../../../providers/PaginationProvider/PaginationProvider'

import useDebouncedValue from '../../../utilities/useDebouncedValue'
import useAbortableFetch from '../../../utilities/useAbortableFetch'

import AuthService from '../../../utilities/services/auth.service'

import { ORGANIZATION_PERMISSION_SLUGS } from '@blocktickets/shared-utils'
import { paymentStatuses, checkPermission } from '../../../utilities/helpers'
import { buildFlexPackAssignmentModalPayload } from '../../../utilities/flexPackAssignment'

import { unifiedSearch } from '../../../utilities/api'

import Card from 'react-bootstrap/Card'
import Button from 'react-bootstrap/Button'

import { SearchHeader } from '../SearchHeader'
import { NoPermissionsContainer } from '../../NoPermissionsContainer'
import { PageLoadingContainer } from '../../PageLoadingContainer'
import { LoadingContainer } from '../../LoadingContainer'
import { EmptyContainer } from '../../EmptyContainer'
import { Pagination } from '../../Pagination'
import AssignTicketsModalContentV2 from '../../../providers/AssignToProvider/AssignTicketsModalContent/AssignTicketsModalContentV2'
import { EventAssignTable } from '../Results/EventAssignTable'

const statusOpts = [
  {
    label: 'All',
    value: 'all',
  },
  ...[
    ...new Map(
      Object.values(paymentStatuses).map((obj) => [JSON.stringify(obj), obj])
    ).values(),
  ],
]

export default function AssignFlexPackages({ uuid }) {
  const didMountRef = useRef(false)

  const organization = AuthService.getOrg()
  const { orgPermissions } = useContext(UserContext)
  const { getPermissions } = AuthService

  const orgPermissionsReady = orgPermissions?.length > 0
  const hasPermission =
    orgPermissionsReady &&
    checkPermission(
      getPermissions(),
      ORGANIZATION_PERMISSION_SLUGS.FLEX_PACKAGES_MANAGEMENT
    )

  const [orders, setOrders] = useState([])
  const [isInitialState, setIsInitialState] = useState(true)
  const [isLoading, setIsLoading] = useState(true)
  const [status, setStatus] = useState(statusOpts[0].value)
  const [searchTerm, setSearchTerm] = useState('')

  const debouncedQuery = useDebouncedValue(searchTerm, 300)
  const abortable = useAbortableFetch()

  const {
    currentPage,
    setCurrentPage,
    perPage,
    pageCount,
    setPageCount,
    resultsPerPageOpts,
    handleChange,
  } = usePagination()

  const runSearch = useCallback(
    async (isFirstTime = false) => {
      setIsLoading(true)
      try {
        const res = await abortable((signal) =>
          unifiedSearch(
            {
              organizationUUID: organization?.[0]?.uuid,
              uuid: uuid,
              by: 'flex-pack',
              sourceType: 'ticket_assignment',
              searchTerm: debouncedQuery,
              paymentStatus: status,
              page: currentPage + 1,
              perPage: perPage,
            },
            signal
          )
        )

        const {
          data,
          meta: {
            pagination: { pageCount: pc },
          },
        } = res.data

        setOrders(data)
        setPageCount(pc)
        setIsLoading(false)
      } catch (err) {
        if (err?.name !== 'CanceledError') {
          console.error(err)
          setIsLoading(false)
        }
      } finally {
        setIsInitialState(isFirstTime)
        setIsLoading(false)
      }
    },
    [
      abortable,
      uuid,
      currentPage,
      perPage,
      debouncedQuery,
      status,
      setPageCount,
      organization,
    ]
  )

  const handleStatusChange = (value) => {
    setStatus(value)
  }

  useEffect(() => {
    if (!didMountRef.current) return
    runSearch()
  }, [currentPage, perPage])

  useEffect(() => {
    if (currentPage === 0 && didMountRef.current) {
      runSearch()
      return
    }

    setCurrentPage(0)
  }, [debouncedQuery, status])

  useEffect(() => {
    if (!orgPermissionsReady || !hasPermission) return
    runSearch(true)
    didMountRef.current = true
  }, [orgPermissionsReady, hasPermission])

  if (!orgPermissionsReady) {
    return <PageLoadingContainer />
  }

  if (!hasPermission) {
    return <NoPermissionsContainer />
  }

  return (
    <>
      <div className="position-relative">
        <section
          className="max-width-wrapper"
          data-testid="assign-flex-packages-section"
        >
          <Header
            organization={organization[0]}
            isInitialState={isInitialState}
            orders={orders}
            status={status}
            handleStatusChange={handleStatusChange}
            setSearchTerm={setSearchTerm}
            runSearch={runSearch}
          />
          <Results
            orders={orders}
            setOrders={setOrders}
            isInitialState={isInitialState}
            isLoading={isLoading}
            runSearch={runSearch}
            pagination={{
              currentPage,
              setCurrentPage,
              perPage,
              pageCount,
              setPageCount,
              resultsPerPageOpts,
              handleChange,
            }}
          />
        </section>
      </div>
    </>
  )
}

function Results({
  orders,
  setOrders,
  isInitialState,
  isLoading,
  pagination,
  runSearch,
}) {
  if (isLoading) {
    return <LoadingContainer />
  }

  if (isInitialState && !orders?.length) {
    return (
      <Card body>
        <EmptyContainer style="center lg">
          <p>No flex package orders</p>
          <p className="subtitle">
            Click Assign package above to assign your first flex package order.
          </p>
        </EmptyContainer>
      </Card>
    )
  }

  if (orders?.length === 0) {
    return (
      <Card body>
        <EmptyContainer style="center lg">
          <p>No results found...</p>
        </EmptyContainer>
      </Card>
    )
  }

  return (
    <>
      <EventAssignTable
        orders={orders}
        setOrders={setOrders}
        runSearch={runSearch}
      />
      <Pagination
        pageCount={pagination.pageCount}
        perPage={pagination.perPage}
        currentPage={pagination.currentPage}
        resultsPerPageOpts={pagination.resultsPerPageOpts}
        handleClick={(value) => pagination.setCurrentPage(value.selected)}
        handleChange={pagination.handleChange}
      />
    </>
  )
}

function Header({
  isInitialState,
  orders,
  organization,
  status,
  handleStatusChange,
  setSearchTerm,
  runSearch,
}) {
  const { flexPack, hasFlexPackEnded, canPublish } = useContext(
    FlexPackDetailsContext
  )
  const { openModal } = useModal()

  const assignDisabled = hasFlexPackEnded || !canPublish || !flexPack?.uuid

  const openAssignModal = () => {
    if (assignDisabled) return

    openModal(
      AssignTicketsModalContentV2,
      buildFlexPackAssignmentModalPayload({
        flexPack,
        organization,
        reloadAssignments: () => runSearch(),
      }),
      { className: 'modal-xl', contentClassName: '' }
    )
  }

  return (
    <SearchHeader
      selectOptions={{
        show: true,
        options: statusOpts,
        value: status,
        handleChange: handleStatusChange,
        label: 'Filter By Status',
        labelId: 'filter-by-status',
      }}
      showActions={!isInitialState || orders?.length > 0}
      rightSideComponent={
        <Button
          variant="outline-light"
          size="lg"
          className={`btn-plus ${assignDisabled ? 'btn-link-disabled' : ''}`}
          onClick={openAssignModal}
          disabled={assignDisabled}
          aria-label="Assign flex package to a buyer"
        >
          Assign package
        </Button>
      }
      title="Assign package"
      desc="Manually add fan information for flex package tickets."
      searchPlaceholder="Search by email"
      setSearchTerm={setSearchTerm}
      data={orders}
      exportedData="invoices"
      flexPackName={flexPack?.name || orders[0]?.flex_pack?.name}
      organizationName={organization?.name}
    />
  )
}
